// import react packages;
import React, { useContext, useState } from "react";
import { useMutation } from "@apollo/react-hooks";
import PropTypes from "prop-types";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";

// Import Polaris Components
import { Banner, Card, PageActions, Stack, TextField } from "@shopify/polaris";

// import helper
import { PrivateContext } from "lib/context";
import { baseHelper, errorHelper } from "lib/helpers";
import constant from "lib/constant/constant";

import { SAVE_DMS_CONFIG } from "app/productOld/apollo/mutations";

// Every DMSMatrix credential is the vendor's own and collected here - nothing is
// shared across vendors or held server-side.
const emptyValue = {
  clientId: "",
  clientSecret: "",
  clientName: "",
  merchantCode: "",
  channelCode: "",
  vendorCode: "",
  depotCode: "",
};

// not every DMSMatrix account is depot-scoped, so this one is accepted blank
const optionalFields = ["depotCode"];

const fieldLabels = {
  clientId: "Client ID",
  clientSecret: "Client Secret",
  clientName: "Client Name",
  merchantCode: "Merchant Code",
  channelCode: "Channel Code",
  vendorCode: "Vendor Code",
  depotCode: "Depot Code",
};

// the secret is the only credential worth masking - the rest are account
// identifiers the vendor needs to be able to read back while typing
const secretFields = ["clientSecret"];

// says what leaving the one optional field blank actually does, so a vendor
// without a depot-scoped account is not left guessing whether it matters
const fieldHelp = {
  depotCode: "Leave blank unless DMSMatrix gave you a depot code - stock is then read across every depot your vendor code can see.",
};

// DMSMatrix has no OAuth handshake - the vendor pastes their own channel/vendor
// codes (mirrors wooCommerceConnect.js's manual key/secret pattern), and
// magic-sauce-dms verifies the full config against DMSMatrix before saving/activating.
const DmsConnect = (props) => {
  const { cms, history, currentUserRefetch } = useContext(PrivateContext);
  const { setBanner } = props;
  const { CRITICAL, SUCCESS } = constant;

  const [value, setValue] = useState(emptyValue);
  const [errors, setErrors] = useState({});
  const [isConnected, setIsConnected] = useState(false);
  const [localBanner, setLocalBanner] = useState({ isOpen: false, status: "", title: "" });

  const [saveDmsConfig, { loading }] = useMutation(SAVE_DMS_CONFIG);

  const handleChange = (field, fieldValue) => {
    setValue({ ...value, [field]: fieldValue });
    setErrors({ ...errors, [field]: "" });
  };

  const validate = () => {
    const nextErrors = {};
    Object.keys(emptyValue).forEach((field) => {
      if (optionalFields.includes(field)) {
        return;
      }
      if (!value[field] || !value[field].trim()) {
        nextErrors[field] = `${fieldLabels[field]} is required`;
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onSubmit = async () => {
    if (!validate()) {
      return;
    }

    try {
      const input = Object.keys(emptyValue).reduce((acc, field) => {
        acc[field] = value[field].trim();
        return acc;
      }, {});

      const response = await saveDmsConfig({ variables: { input } });

      const responseError = baseHelper.getResponseError(response.data, "saveDmsConfig");
      const responseData = baseHelper.getResponseData(response.data, "saveDmsConfig");

      if (responseError) {
        setLocalBanner({ isOpen: true, status: CRITICAL, title: responseError });
        return;
      }

      if (responseData) {
        setIsConnected(true);
        const successBanner = { isOpen: true, status: SUCCESS, title: cms("section.connect.message.success.connected") || "DMS connected" };
        setLocalBanner(successBanner);
        setBanner(successBanner);
        // connection state lives on currentUser.storeConfig.dms - refresh it before
        // navigating so the connect page doesn't ask the vendor to connect again
        if (currentUserRefetch) {
          await currentUserRefetch();
        }
        history.push("/products/dms");
      }
    } catch (exception) {
      setLocalBanner({ isOpen: true, status: CRITICAL, title: errorHelper.parse(exception) });
    }
  };

  return (
    <Card title={cms("section.connect.title") || "Connect DMSMatrix"}>
      <Card.Section>
        {localBanner.isOpen && (
          <>
            <Banner
              isOpen={localBanner.isOpen}
              status={localBanner.status}
              title={localBanner.title}
              onDismiss={() => setLocalBanner({ isOpen: false })}
            />
            <br />
          </>
        )}

        <Stack vertical>
          {Object.keys(emptyValue).map((field) => (
            <TextField
              key={field}
              label={optionalFields.includes(field) ? `${fieldLabels[field]} (optional)` : `${fieldLabels[field]}*`}
              id={field}
              type={secretFields.includes(field) ? constant.PASSWORD : constant.TEXT}
              value={value[field]}
              error={errors[field]}
              helpText={fieldHelp[field]}
              onChange={(inputValue) => handleChange(field, inputValue)}
              disabled={isConnected}
            />
          ))}
        </Stack>
      </Card.Section>

      {!isConnected && (
        <Card.Section>
          <PageActions
            primaryAction={{
              id: "connectDms",
              content: cms("section.connect.button.connect") || "Connect",
              onAction: onSubmit,
              loading,
            }}
            secondaryActions={[
              {
                id: "cancelAction",
                content: cms("section.connect.button.cancel") || "Cancel",
                onAction: () => history.push("/products"),
              },
            ]}
          />
        </Card.Section>
      )}
    </Card>
  );
};

DmsConnect.propTypes = {
  setBanner: PropTypes.func,
};

DmsConnect.defaultProps = {
  setBanner: () => {},
};

export default withFeature(withErrorBoundary(DmsConnect), { feature: constant.DMS_PRODUCT });
